<template>
  <div class="card mb-25 border-0 rounded-0 bg-white letter-spacing">
    <div class="card-head box-shadow bg-white d-lg-flex align-items-center justify-content-between p-15 p-sm-20 p-md-25">
      <div class="d-sm-flex align-items-center">
        <button 
          class="default-btn position-relative transition border-0 fw-medium text-white pt-11 pb-11 ps-25 pe-25 pt-md-12 pb-md-12 ps-md-30 pe-md-30 rounded-1 bg-success fs-md-15 fs-lg-16 d-inline-block me-10 mb-10 mb-lg-0 text-decoration-none"
          type="button"
          @click="openAddModal"
        >
          Ajouter un compte prépayé
        </button>
      </div>
    </div>

    <div class="card-body p-15 p-sm-20 p-md-25">
      <div class="table-responsive">
        <table class="table text-nowrap align-middle mb-0">
          <thead>
            <tr>
              <th>N° Compte</th>
              <th>Acteur</th>
              <th>Solde</th>
              <th>Devise</th>
              <th>Limite crédit</th>
              <th>Statut</th>
              <th class="text-end">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="loading">
              <td colspan="7" class="text-center">
                <div class="spinner-border text-primary" role="status">
                  <span class="visually-hidden">Chargement...</span>
                </div>
              </td>
            </tr>
            <tr v-else-if="prepaidAccounts.length === 0">
              <td colspan="7" class="text-center">Aucun compte prépayé trouvé</td>
            </tr>
            <tr v-else v-for="account in prepaidAccounts" :key="account.id">
              <td class="fw-medium text-primary-emphasis">{{ account.accountNumber }}</td>
              <td class="fw-medium text-secondary-emphasis">
                {{ account.acteur?.nomComplet || 'N/A' }}
              </td>
              <td class="fw-bold" :class="getBalanceClass(account.balance)">
                {{ formatAmount(account.balance) }}
              </td>
              <td>{{ account.currencyCode }}</td>
              <td>{{ account.creditLimit ? formatAmount(account.creditLimit) : 'Aucune' }}</td>
              <td>
                <span class="badge" :class="getStatusClass(account.status)">
                  {{ getStatusLabel(account.status) }}
                </span>
              </td>
              <td class="shadow-none lh-1 fw-medium text-body-tertiary text-end pe-0">
                <div class="dropdown">
                  <span 
                    class="badge text-white bg-primary fs-15 dropdown-toggle"
                    data-bs-toggle="dropdown" 
                    aria-expanded="false"
                    style="cursor: pointer;"
                  >
                    Actions
                    <i class="flaticon-chevron-2 position-relative ms-5 top-2 fs-15"></i>
                  </span>
                  <ul class="dropdown-menu">
                    <li>
                      <a class="dropdown-item d-flex align-items-center" 
                        href="javascript:void(0)" 
                        @click="viewDetails(account.id)">
                        <i class="flaticon-view lh-1 me-8 position-relative top-1"></i>
                        Voir détails
                      </a>
                    </li>
                    <li>
                      <a class="dropdown-item d-flex align-items-center" 
                        href="javascript:void(0)" 
                        @click="recharger(account)">
                        <i class="flaticon-plus lh-1 me-8 position-relative top-1"></i>
                        Recharger
                      </a>
                    </li>
                    <li><hr class="dropdown-divider"></li>
                    <li>
                      <a class="dropdown-item d-flex align-items-center" 
                        href="javascript:void(0)" 
                        @click="editAccount(account.id)">
                        <i class="flaticon-pen lh-1 me-8 position-relative top-1"></i>
                        Modifier
                      </a>
                    </li>
                    <li v-if="account.status === 'ACTIVE'">
                      <a class="dropdown-item d-flex align-items-center text-warning" 
                        href="javascript:void(0)" 
                        @click="suspendAccount(account.id)">
                        <i class="flaticon-pause lh-1 me-8 position-relative top-1"></i>
                        Suspendre
                      </a>
                    </li>
                    <li v-if="account.status === 'SUSPENDED'">
                      <a class="dropdown-item d-flex align-items-center text-success" 
                        href="javascript:void(0)" 
                        @click="activateAccount(account.id)">
                        <i class="flaticon flaticon-check lh-1 me-8 position-relative top-1"></i>
                        Activer
                      </a>
                    </li>
                    <li>
                      <a class="dropdown-item d-flex align-items-center text-danger" 
                        href="javascript:void(0)" 
                        @click="deleteAccount(account.id)">
                        <i class="flaticon-delete lh-1 me-8 position-relative top-1"></i>
                        Supprimer
                      </a>
                    </li>
                  </ul>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <AddModalPrepaidAccount 
      v-if="showAddModal"
      :item="selectedAccountId" 
      @close="handleCloseAdd" 
      @openmodal="openModal" 
    />
    <RechargeModal 
      v-if="showRechargeModal"
      :account="selectedAccount" 
      @close="handleCloseRecharge" 
      @openmodal="openRechargeModal" 
    />
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted } from 'vue';
import ApiService from '@/services/ApiService';
import { error, success } from '@/utils/utils';
import { Modal } from 'bootstrap';
import AddModalPrepaidAccount from '@/components/Compte/AddModalPrepaidAccount.vue';
import RechargeModal from '@/components/Compte/RechargeModal.vue';

export default defineComponent({
  name: "ListePrepaidAccount",
  components: { AddModalPrepaidAccount, RechargeModal },
  setup() {
    const prepaidAccounts = ref<any[]>([]);
    const loading = ref(true);
    const selectedAccountId = ref('');
    const selectedAccount = ref<any>(null);

    const showAddModal = ref(false);
    const showRechargeModal = ref(false);

    const loadPrepaidAccounts = async () => {
      loading.value = true;
      try {
        const response = await ApiService.get('/prepaid-account?limit=100');
        prepaidAccounts.value = response.data.data.data;
      } catch (err) {
        error('Erreur lors du chargement des comptes prépayés');
      } finally {
        loading.value = false;
      }
    };

    const formatAmount = (amount: number | null) => {
      if (amount === null || amount === undefined) return '0.00';
      return new Intl.NumberFormat('fr-FR', { 
        minimumFractionDigits: 2, 
        maximumFractionDigits: 2 
      }).format(amount);
    };

    const getBalanceClass = (balance: number) => {
      if (balance < 0) return 'text-danger';
      if (balance === 0) return 'text-warning';
      return 'text-success';
    };

    const getStatusClass = (status: string) => {
      return {
        'ACTIVE': 'bg-success',
        'SUSPENDED': 'bg-warning',
        'CLOSED': 'bg-danger'
      }[status] || 'bg-secondary';
    };

    const getStatusLabel = (status: string) => {
      return { 
        ACTIVE: 'Actif', 
        SUSPENDED: 'Suspendu', 
        CLOSED: 'Fermé' 
      }[status] || status;
    };

    const editAccount = (id: string) => {
      selectedAccountId.value = id;
      showAddModal.value = true;

      setTimeout(() => {
        const modalElement = document.getElementById('AddModalPrepaidAccount');
        if (modalElement) {
          const modal = new Modal(modalElement);
          modal.show();
        }
      }, 100);
    };

    const recharger = (account: any) => {
      selectedAccount.value = account;
      showRechargeModal.value = true;
      
      setTimeout(() => {
        const modalElement = document.getElementById('RechargeModal');
        if (modalElement) {
          const modal = new Modal(modalElement);
          modal.show();
        }
      }, 100);
    };

    const openAddModal = async () => {
      try {
        console.log(' Vérification des acteurs disponibles...');
        const response = await ApiService.get('/prepaid-account/user-acteurs-creation');
        
        console.log(' Réponse API complète:', response.data);

        const acteursData = response.data.data || [];
        
        console.log(' Acteurs disponibles:', acteursData);
        
        if (acteursData.length === 0) {
          error(' Aucun acteur disponible. Tous vos acteurs ont déjà un compte prépayé actif.');
          return;
        }
        
        selectedAccountId.value = '';
        showAddModal.value = true;

        setTimeout(() => {
          const modalElement = document.getElementById('AddModalPrepaidAccount');
          if (modalElement) {
            const modal = new Modal(modalElement);
            modal.show();
          }
        }, 100);
      } catch (err: any) {
        console.error(' Erreur vérification acteurs:', err);
        error('Erreur lors de la vérification des acteurs disponibles');
      }
    };

    const handleCloseAdd = () => {
      showAddModal.value = false;
      selectedAccountId.value = '';
      loadPrepaidAccounts();
    };

    const handleCloseRecharge = () => {
      showRechargeModal.value = false;
      selectedAccount.value = null;
      loadPrepaidAccounts();
    };

    const openModal = (el: HTMLElement) => {
      if (el) {
        const modal = new Modal(el);
        modal.show();
      }
    };

    const openRechargeModal = (el: HTMLElement) => {
      if (el) {
        const modal = new Modal(el);
        modal.show();
      }
    };

    const viewDetails = (id: string) => {
      window.location.href = `/prepaid-account/${id}`;
    };

    const suspendAccount = async (id: string) => {
      if (confirm('Suspendre ce compte ?')) {
        try {
          await ApiService.post(`/prepaid-account/${id}/suspend`, {});
          success('Compte suspendu');
          loadPrepaidAccounts();
        } catch (err: any) {
          error(err.response?.data?.message || 'Erreur lors de la suspension');
        }
      }
    };

    const activateAccount = async (id: string) => {
      if (confirm('Réactiver ce compte ?')) {
        try {
          await ApiService.post(`/prepaid-account/${id}/activate`, {});
          success('Compte activé');
          loadPrepaidAccounts();
        } catch (err: any) {
          error(err.response?.data?.message || "Erreur lors de l'activation");
        }
      }
    };

    const deleteAccount = async (id: string) => {
      if (confirm('Supprimer définitivement ce compte ?')) {
        try {
          await ApiService.delete(`/prepaid-account/${id}`);
          success('Compte supprimé');
          loadPrepaidAccounts();
        } catch (err: any) {
          error(err.response?.data?.message || 'Erreur lors de la suppression');
        }
      }
    };

    onMounted(() => {
      loadPrepaidAccounts();
    });

    return {
      prepaidAccounts,
      loading,
      formatAmount,
      getBalanceClass,
      getStatusClass,
      getStatusLabel,
      selectedAccountId,
      selectedAccount,
      showAddModal, 
      showRechargeModal, 
      editAccount,
      recharger,
      openAddModal, 
      handleCloseAdd, 
      handleCloseRecharge, 
      openModal, 
      openRechargeModal,
      viewDetails,
      suspendAccount,
      activateAccount,
      deleteAccount,
      loadPrepaidAccounts
    };
  }
});
</script>